import { z } from "zod";

/**
 * `PRICE-NEGOTIATION` N2, **contract first** (founder D-1102-14; the architect's orders and addenda of 2 November 2026; designer R's batch 7, frames 102 to 108, `dd5d28e3…`). Contract only: no door answers these yet (the door PR, after the minimum price
 * lands, because the door's floor check reads it).
 *
 * **The product.** The dealer marks each car « prix négociable » or « prix ferme ». On a negotiable car a client may send a price OFFER; the dealer accepts, declines or COUNTERS; the client may accept, decline or counter back.
 * A firm-price car takes no offers (lane AA's availability queue serves it). Only a SERIOUS client may offer (a full account, a complete profile, a positive eligibility answer); one live offer per client per car; a limited number of rounds
 * (two counters each, then « dernier prix »); an offer EXPIRES without an answer; the dealer's per-car FLOOR declines automatically, below it, and the offer never reaches him. Accepting an offer counts as « disponible » for that
 * client: the car is held for him (the hold), the other offers stay until their own expiry, and if the hold lapses the dealer may accept the next. « Indisponible » or a withdrawal collapses the board and every offerer is told. Engaging a
 * dossier purges the client's other offers and queue entries.
 *
 * **What the dealer NEVER receives (addendum N-6, ruling N-7).** Never income, debts, credit strength, a grade, and **no eligibility key at all**: every live offer is from an eligible client, which the board's header says ONCE, in a
 * served sentence. There is no « non éligible » state anywhere, and the dealer's offer type carries no field that could hold one (the parity spec holds the key set).
 *
 * **Served, never composed on a screen (D-1020-7).** The words (the header, the rule line, the price-gap pointer, the floor's consequence, the range, the age and expiry phrases, the hold's countdown), the figures (every amount, the
 * remaining times, the counts) and the DURATIONS (offer and counter answer window, the hold, the rounds) come from the server, from settings, with the server's `asOf`; no screen computes an age, a countdown or a range. The words below
 * are PROPOSED (the A-H architect's) where the order marks them so.
 *
 * **Wire values are `ferme` and `negociable`** (no accent, as `recue` and `vendeur`; the architect's ruling of 2 November amends the order's « négociable » by record). A screen prints the SERVED words, never the enum.
 * **No client name, no initials, on any dealer answer** (no consent before a dossier, D-1031-18; #917's verdict): a card is identified by its `id` and its amount alone.
 *
 * **One definition, two readings** (`DECISION-ANSWER-LENIENT`'s pattern): `z.strictObject` is the contract (stubs, the OpenAPI parity spec), `z.looseObject` the READ twin a client parses the live answer with. The request bodies are strict only.
 *
 * **The doors (to be bound by the door PR; paths PROPOSED, to ratify on quote, beside N1's `/api/v1/me/vehicles/{id}/queue` and `/api/v1/me/queue-board`).**
 *  - dealer (`/api/v1/me/dealership/…`, right `gererInventaire`): `PUT /api/v1/me/dealership/vehicles/{id}/price-mode` (body {@link priceModeBodySchema}, answer {@link priceModeAnswerSchema});
 *    `GET /api/v1/me/dealership/vehicles/{id}/offers` ({@link dealerOffersAnswerSchema}); `POST …/offers/{offerId}/accept` ({@link dealerAcceptBodySchema}), `…/decline` ({@link dealerDeclineBodySchema}), `…/counter`
 *    ({@link counterBodySchema}); `POST …/offers/accept-next` ({@link dealerAcceptNextBodySchema}); each action answers the board again.
 *  - client (`/api/v1/me/…`): `GET /api/v1/me/vehicles/{id}/offer` ({@link clientOfferAnswerSchema}); `POST …/offer` ({@link offerBodySchema}), `…/offer/counter` ({@link counterBodySchema}), `…/offer/accept`, `…/offer/decline`,
 *    `…/offer/withdraw` (empty bodies); each answers the client's view again.
 */

export const PRICE_MODES = ["ferme", "negociable"] as const;

/** `catalog.price_offers.status`. The dealer's board lists LIVE offers; a closed offer carries one of {@link OFFER_CLOSE_REASONS}. */
export const OFFER_STATES = [
  "awaiting_dealer",
  "awaiting_client",
  "accepted",
  "declined",
  "auto_declined",
  "expired",
  "withdrawn",
  "purged",
] as const;

/**
 * Why an offer left the board (every closure is told, A47: it may close, never in silence). `eligibility_lapsed` (addendum N-6): the client's served eligibility lapsed while the offer was live (a stale commitment, a profile made
 * incomplete); the offer is WITHDRAWN and BOTH sides are told in-app. The dealer's notice says only that the client no longer meets the conditions, never why (the dealer never learns the client's situation).
 */
export const OFFER_CLOSE_REASONS = [
  "declined_by_dealer",
  "declined_by_client",
  "below_floor",
  "expired",
  "withdrawn_by_client",
  "hold_lapsed",
  "dealer_unavailable",
  "vehicle_withdrawn",
  "buyer_engaged",
  "buyer_engaged_at_listed_price",
  "client_engaged_elsewhere",
  "eligibility_lapsed",
] as const;

/**
 * **The collapse of a car's board, one reason code and TWO served sentences, one per audience** (A-H note of 2 November; the words are PROPOSED). A collapse closes every live offer on the car and EVERY offerer is told.
 *  - `dealer_unavailable`: the dealer marked the car « indisponible ».
 *  - `vehicle_withdrawn`: the car left the catalogue.
 *  - `buyer_engaged`: the client whose offer the dealer ACCEPTED opened his dossier.
 *  - `buyer_engaged_at_listed_price`: a client opened a dossier on a negotiable car WITHOUT an accepted offer, at the listed price. **The birth door has no guard against it today**: its only refusal is a car held by another buyer
 *    (`me-dossiers.controller.ts:803-805`, « Un acheteur est engagé sur ce véhicule. »), so a client may open a file at the listed price on a negotiable car with no offer; the dealer's `buyer_engaged` sentence would be FALSE here
 *    (he accepted no offer), so this form has its own code and its own dealer sentence (PROPOSED, A-H). N2 serves the form matching the engaging dossier's ORIGIN (an accepted offer, or the listed price), read from what the
 *    birth recorded (the agreed price present or absent), never guessed. The client's sentence is the same for both forms.
 * The dealer's sentence carries, appended on every cause, « Chaque client en a été prévenu. » ({@link OFFER_TOLD_WORDS}).
 */
export const OFFER_COLLAPSE_CAUSES = [
  "dealer_unavailable",
  "vehicle_withdrawn",
  "buyer_engaged",
  "buyer_engaged_at_listed_price",
] as const;

/** PROPOSED (A-H), verbatim. */
export const OFFER_COLLAPSE_WORDS = {
  dealer_unavailable: {
    dealer: "Vous avez marqué cette voiture indisponible.",
    client: "Cette voiture a été marquée indisponible.",
  },
  vehicle_withdrawn: {
    dealer: "Vous avez retiré cette voiture du catalogue.",
    client: "Le vendeur a retiré cette voiture du catalogue.",
  },
  buyer_engaged: {
    dealer: "Le client dont vous avez accepté l'offre a ouvert son dossier.",
    client: "Un autre client a ouvert son dossier sur cette voiture.",
  },
  buyer_engaged_at_listed_price: {
    dealer: "Un client a ouvert son dossier sur cette voiture au prix affiché.",
    client: "Un autre client a ouvert son dossier sur cette voiture.",
  },
} as const;

/**
 * The CLIENT's sentence for every closure NOT caused by a collapse of the board (the four collapse causes are {@link OFFER_COLLAPSE_WORDS}'), quoted from `claude/n2-client-words-2026-11-03.md` (A-H, PROPOSED, ruling of 3
 * November). `eligibility_lapsed` names the car, so every entry is a function of the vehicle's title. Served by the offer view AND by the notice (`notifications`), so there is one text.
 */
export const OFFER_CLOSURE_WORDS = {
  eligibility_lapsed: (title: string) =>
    `Votre offre sur ${title} a été retirée : votre éligibilité n'est plus confirmée. Vérifiez à nouveau votre éligibilité ; si elle est confirmée, vous pourrez refaire une offre.`,
  client_engaged_elsewhere: () =>
    "Vous avez ouvert un dossier sur une autre voiture : cette offre est close.",
  withdrawn_by_client: () => "Vous avez retiré votre offre.",
  declined_by_dealer: () => "Le vendeur a refusé votre offre.",
  declined_by_client: () => "Vous avez refusé la contre-proposition du vendeur.",
  expired: () => "Votre offre a expiré sans réponse.",
  hold_lapsed: () => "La réservation a expiré. Vous n'avez pas ouvert votre dossier à temps.",
  below_floor: () =>
    "Le vendeur n'accepte pas d'offre à ce montant. Vous pouvez proposer un montant plus élevé.",
} as const;

/**
 * The HOLDER's own closure, served BY FACT (A-H, ruled 3 November): the client whose offer led to his dossier is not told another client opened one. « Au prix convenu » only when the dossier was born with the agreed
 * price (N3); otherwise the listed-price form.
 */
export const OFFER_HOLDER_ENGAGED_WORDS = {
  agreedPrice:
    "Vous avez ouvert votre dossier sur cette voiture au prix convenu : la négociation est terminée.",
  listedPrice: "Vous avez ouvert votre dossier sur cette voiture : la négociation est terminée.",
} as const;

/** A-H's word (ruled 3 November, « valable »): what a dealership reads when an offer lapsed for eligibility. **It never says why** (no eligibility, no profile, nothing of the client): the door's refusal and the notice's body. */
export const OFFER_LAPSE_DEALER_WORDS = {
  refusal: "Cette offre n'est plus valable.",
  notice: "Une offre sur cette voiture n'est plus valable.",
} as const;

/** PROPOSED (A-H, 3 November): what the dealership reads when the client RELEASES his hold; the next offer is then accepted as served. */
export const OFFER_HOLD_RELEASED_DEALER_WORDS =
  "Le client a renoncé à sa réservation : la voiture est de nouveau disponible." as const;

/** PROPOSED (A-H): appended to the dealer's sentence on every cause. */
export const OFFER_TOLD_WORDS = "Chaque client en a été prévenu." as const;

/** The two causes that mean a client opened a dossier on the car. */
export const ENGAGED_CAUSES: readonly string[] = ["buyer_engaged", "buyer_engaged_at_listed_price"];

export const HOLD_STATES = ["held", "lapsed"] as const;

const buildPriceOffers = (object: typeof z.strictObject) => {
  const amount = z.number().int().min(1);
  const nullableAmount = amount.nullable();

  /** The settings the plan serves; no screen computes them (D-1020-7). The hold is the negotiated one for a negotiable car and the queue's for a firm one. */
  const durations = object({
    /** ONE served answer window for an offer AND a counter (default 48 h, from settings; the architect's ruling of 2 November). The frame's 24 h is a drawing. */
    answerWindowHours: z.number().int().min(1),
    /** The accepted client's window to open his dossier (the position hold). Default 72 negotiated, 24 firm (the architect's ruling). */
    holdHours: z.number().int().min(1),
    /** Counters each side may make before « dernier prix » (two). */
    countersPerSide: z.number().int().min(0),
  });

  // ------------------------------------------------------------------------------------------- the car's mode and floor

  /** `PUT …/price-mode`'s answer, and the fiche's block: the mode, the floor and its consequence in words. */
  const priceMode = object({
    priceMode: z.enum(PRICE_MODES),
    /** The dealer's per-car floor, whole FCFA, `null` when none (and always `null` on a firm car). */
    floorAmount: nullableAmount,
    /** The platform minimum, the floor's own lower bound (`MINIMUM-VEHICLE-PRICE`); `null` while unconfigured. */
    minimumPriceAmount: nullableAmount,
    /** « Offres sous {plancher} FCFA refusées automatiquement. » or « Sans plancher, toute offre reste visible. » (PROPOSED); `null` on a firm car. */
    floorSentence: z.string().nullable(),
  });

  /** One exchange of the negotiation, from `price_offer_events` (additive, N2 door): who, how much, and WHEN in served words (`ageWords`). */
  const historyEntry = object({
    actor: z.enum(["dealer", "client"]),
    amount,
    whenWords: z.string().min(1),
  });

  /** The served range of a counter: the bounds and their sentence (D-1020-7), for either side. */
  const counterBounds = object({
    minAmount: amount,
    maxAmount: amount,
    rangeSentence: z.string().min(1),
  });

  // --------------------------------------------------------------------------------------------- the dealer's offers board
  /**
   * One offer, as the DEALER reads it. **Keys are exactly what the record allows**: the amount, whether a deposit is simulated (« Apport simulé »), the age and the expiry, the round. No eligibility, no income, no debts, no grade, and **NO NAME OR INITIALS**
   * (the architect's ruling of 2 November: no consent before a dossier).
   */
  const dealerOffer = object({
    id: z.string().min(1),
    amount,
    /** « Apport simulé » (PROPOSED, A-H): whether the client ran a simulation on THIS car with a deposit above zero. ONE name for that fact across the negotiation (`depositSimulated`; N1 in #924 does the same); `depositDeclared` does not exist here. */
    depositSimulated: z.boolean(),
    /** The state the card is in: the dealer's turn, a counter out to the client, the accepted one, or listed with no action while another is held. */
    state: z.enum(["awaiting_dealer", "awaiting_client", "accepted", "listed"]),
    createdAt: z.string(),
    expiresAt: z.string(),
    /** « il y a 2 h » and « l'offre expire dans 22 h », composed on the server from `asOf`. */
    ageWords: z.string().min(1),
    expiryWords: z.string().min(1),
    /** 1-based: the offers and counters made on this offer so far, in rounds. */
    round: z.number().int().min(1),
    /** Counters the dealer may still make on it. `0` is « Dernier prix ». */
    countersLeft: z.number().int().min(0),
    /** « Dernier prix »: no counter is left, the card shows the chip and « Contre-proposer » is gone. */
    lastPrice: z.boolean(),
    /** The exchanges so far, oldest first (additive, N2 door). */
    history: z.array(historyEntry),
    /** What the dealer may do now. All `false` on a card that is not his turn (`awaiting_client`) or while another offer is held. */
    actions: object({ accept: z.boolean(), decline: z.boolean(), counter: z.boolean() }),
    /**
     * The counter dialog's served bounds, present exactly when `actions.counter` is true. **A dealer's counter is above the offer and at most the listed price**, and at or above the floor and the platform minimum
     * (D-1102-10): `minAmount` is the highest of those lower bounds, `maxAmount` the listed price. Words served (« Son délai pour répondre » is the served answer window).
     */
    counter: counterBounds.nullable(),
  });

  /** The accepted client's hold, as the dealer reads it. There is NO release door for the dealer (architect's ruling): the hold ends by its expiry or the client's own release. */
  const dealerHold = object({
    state: z.enum(HOLD_STATES),
    offerId: z.string().min(1),
    /** The accepted amount. */
    amount,
    expiresAt: z.string(),
    /** The served title (« La voiture est réservée pour un client » / « La réservation a expiré »): no name. */
    title: z.string().min(1),
    /** « Il reste 23 h 12 min à ce client pour ouvrir son dossier »; `null` once lapsed. */
    countdownWords: z.string().nullable(),
    /** The one sentence on withdrawing a car under the existing rules (no « Libérer la voiture »). */
    withdrawalSentence: z.string().min(1),
    /** The next live offer (its id and amount, no name), when the hold has lapsed; `null` otherwise or when none is left. */
    nextOffer: object({ id: z.string().min(1), amount }).nullable(),
  });

  const dealerOffers = object({
    asOf: z.string(),
    vehicleId: z.string().min(1),
    priceMode: z.enum(PRICE_MODES),
    /** The price displayed on the car (never rewritten by an offer). */
    listedPriceAmount: amount,
    floorAmount: nullableAmount,
    /** The tab's count (live offers). */
    offerCount: z.number().int().min(0),
    /** « Toutes les offres viennent de clients éligibles. » (PROPOSED, served). */
    headerSentence: z.string().min(1),
    /** « Vous voyez le montant et l'apport simulé, jamais les revenus ni les dettes du client. » */
    ruleSentence: z.string().min(1),
    /** « Si l'expertise ressort sous le prix convenu, l'écart se règle sur la page de la voiture (« Écart de prix »). » */
    priceGapSentence: z.string().min(1),
    durations,
    hold: dealerHold.nullable(),
    /** Present when the client released his hold and no hold stands: « Le client a renoncé à sa réservation : la voiture est de nouveau disponible. » (additive, N2 door); `null` otherwise. */
    holdReleasedWords: z.string().min(1).nullable(),
    /**
     * Present when the car's board has COLLAPSED (every offer closed, every offerer told): the cause and the DEALER's sentence ({@link OFFER_COLLAPSE_WORDS}), with {@link OFFER_TOLD_WORDS} (« Chaque client en a été prévenu. »)
     * served beside it, on every cause and both forms of « buyer engaged ».
     */
    collapse: object({
      cause: z.enum(OFFER_COLLAPSE_CAUSES),
      causeWords: z.string().min(1),
      toldWords: z.string().min(1),
      /**
       * The dealer's OWN dossier that engaged the car (« Ouvrir ce dossier », frame 110c), set ONLY for `buyer_engaged` and `buyer_engaged_at_listed_price`, `null` for `dealer_unavailable` and `vehicle_withdrawn`
       * (additive, N2 door). It is the dealer's dossier (`seller_organisation_id` his concession), already in his list. **NEVER served on a client's offer or closure**: no other client learns a dossier id.
       */
      dossierId: z.string().min(1).nullable(),
    })
      .refine((collapse) => collapse.dossierId === null || ENGAGED_CAUSES.includes(collapse.cause), {
        message: "dossierId only accompanies a collapse caused by an engagement",
        path: ["dossierId"],
      })
      .nullable(),
    /** Amount descending, then oldest first. */
    offers: z.array(dealerOffer),
  });

  // -------------------------------------------------------------------------------------------------- the client's offer

  /** What the client sees of his own offer. He sees his own state and the dealer's counter, never the other offers. */
  const clientOffer = object({
    id: z.string().min(1),
    vehicleId: z.string().min(1),
    state: z.enum(OFFER_STATES),
    /** His amount (the standing one). */
    amount,
    /** The dealer's counter awaiting his answer, `null` when none. */
    standingCounterAmount: nullableAmount,
    round: z.number().int().min(1),
    countersLeft: z.number().int().min(0),
    lastPrice: z.boolean(),
    createdAt: z.string(),
    expiresAt: z.string().nullable(),
    expiryWords: z.string().nullable(),
    /** The exchanges so far, oldest first (additive, N2 door). */
    history: z.array(historyEntry),
    /** What he may do now. */
    actions: object({
      accept: z.boolean(),
      decline: z.boolean(),
      counter: z.boolean(),
      withdraw: z.boolean(),
    }),
    /**
     * His counter's served bounds, present exactly when `actions.counter` is true: **above his previous amount and below the dealer's last counter**, and at or above the floor and the platform minimum (D-1102-10).
     * Two counters each, then « dernier prix » (`lastPrice`, `countersLeft`), served.
     */
    counter: counterBounds.nullable(),
    /**
     * The confirmation sentence of the gesture the withdraw button makes (served, A-H of 3 November): « Retirer votre offre ? … » on a live offer, « Libérer la voiture ? … » on an accepted one (the release of his hold);
     * `null` when he may not withdraw (additive, N2 door).
     */
    confirmWords: z.string().nullable(),
    /** His hold once accepted: the window to open his dossier, `null` otherwise. */
    hold: object({ expiresAt: z.string(), countdownWords: z.string().nullable() }).nullable(),
    /** Present on a closed offer: why, in a code and in words (every closure is told). */
    closed: object({
      reason: z.enum(OFFER_CLOSE_REASONS),
      /** The CLIENT's sentence for the cause ({@link OFFER_COLLAPSE_WORDS}), served. */
      words: z.string().min(1),
      /** `true` when A-H have not worded this closure for the client yet: `words` is then the reason code itself, never an invented sentence (additive, N2 door). */
      wordsOwed: z.boolean(),
    }).nullable(),
  });

  const clientOffers = object({
    asOf: z.string(),
    vehicleId: z.string().min(1),
    priceMode: z.enum(PRICE_MODES),
    listedPriceAmount: amount,
    durations,
    /** May this client offer on this car now? `false` carries the code and the words (a firm car, no account, an incomplete profile, no positive eligibility, a live offer already). */
    mayOffer: object({
      allowed: z.boolean(),
      refusal: object({
        code: z.enum([
          "firm_price",
          "no_account",
          "profile_incomplete",
          "not_eligible",
          "offer_exists",
          "own_vehicle",
          "not_for_sale",
        ]),
        words: z.string().min(1),
      }).nullable(),
    }),
    /** His live offer, or the latest closed one so he is told why; `null` when he never offered. */
    offer: clientOffer.nullable(),
  });

  return { durations, priceMode, dealerOffer, dealerHold, dealerOffers, clientOffer, clientOffers };
};

const strict = buildPriceOffers(z.strictObject);
const loose = buildPriceOffers(z.looseObject);

export const offerDurationsSchema = strict.durations;
export const priceModeAnswerSchema = strict.priceMode;
export const dealerOfferSchema = strict.dealerOffer;
export const dealerHoldSchema = strict.dealerHold;
export const dealerOffersAnswerSchema = strict.dealerOffers;
export const clientOfferSchema = strict.clientOffer;
export const clientOfferAnswerSchema = strict.clientOffers;
/** The READ twins: what a client parses the live answers with. */
export const priceModeAnswerReadSchema = loose.priceMode;
export const dealerOffersAnswerReadSchema = loose.dealerOffers;
export const clientOfferAnswerReadSchema = loose.clientOffers;

// ------------------------------------------------------------------------------------------------------ request bodies

/** `PUT /api/v1/me/dealership/vehicles/{id}/price-mode` (dealer). A floor is only meaningful on a negotiable car and is refused on a firm one; it is whole FCFA and never below the platform minimum. */
export const priceModeBodySchema = z.strictObject({
  priceMode: z.enum(PRICE_MODES),
  floorAmount: z.number().int().min(1).nullable().optional(),
});

/** The client's offer. The amount is whole FCFA; the door refuses an amount that would not be a price the dealer could set (the platform minimum) and an amount above the listed price. */
export const offerBodySchema = z.strictObject({ amount: z.number().int().min(1) });
/** Either side's counter. */
export const counterBodySchema = z.strictObject({ amount: z.number().int().min(1) });
export const dealerAcceptBodySchema = z.strictObject({});
export const dealerDeclineBodySchema = z.strictObject({});
/** The hold lapsed: accept the next live offer. The next offer is named by the board; the body names it so the door can refuse a stale board. */
export const dealerAcceptNextBodySchema = z.strictObject({ offerId: z.string().min(1) });

export type PriceMode = (typeof PRICE_MODES)[number];
export type OfferCloseReason = (typeof OFFER_CLOSE_REASONS)[number];
export type PriceModeAnswer = z.infer<typeof priceModeAnswerSchema>;
export type DealerOffer = z.infer<typeof dealerOfferSchema>;
export type DealerOffersAnswer = z.infer<typeof dealerOffersAnswerSchema>;
export type ClientOfferAnswer = z.infer<typeof clientOfferAnswerSchema>;
